"use client";

import Icon from "@/components/common/Icon";
import LibraryBadges from "@/components/common/LibraryBadges";
import { useLocale, useLocalizedData } from "@/context/LocaleContext";
import { useSearch } from "@/context/SearchContext";
import { Branding } from "@/types/branding";
import { I18nData } from "@/types/i18n";
import { Journal } from "@/types/journal";
import {
  generatePaymentLink,
  generatePublishingModelLink,
  generateJournalLink,
  generateLicenseLink,
} from "@/utils/links";
import React, { memo, useRef, useState } from "react";
import { useEscSuppressibleTooltip } from "@/hooks/use-esc-suppressible-tooltip";
import CollapsibleRow from "./collapsible-row";
import MultiLicenseCell from "./multi-license-cell";
import LicenseCell from "./license-cell";
import Linkify from "linkify-react";
import { AgreementModal } from "./agreement-modal";
import { trackUserEvent } from "@/app/api/client-only";
import { QualityScoreEntry } from "./quality-score-entry";
import { ColumnKey, resolveColumnOrder } from "./column-defs";

interface Props {
  journal: Journal;
  i18nData: I18nData;
  branding: Branding | null;
}

const Row = memo(function Row({ journal, i18nData, branding }: Props) {
  const localizedData = useLocalizedData(i18nData);
  const { locale } = useLocale();
  const { slug } = useSearch();
  const [isAgreementOpen, setIsAgreementOpen] = useState(false);
  const paymentButtonRef = useRef<HTMLButtonElement>(null);
  const paymentTooltip = useEscSuppressibleTooltip();
  const publishingModelTooltip = useEscSuppressibleTooltip();

  const showAgreementModal =
    branding?.show_agreement_description && journal.agreement_id;

  // Get display data
  const paymentIcon = journal.payment
    ? localizedData?.payments?.[journal.payment]
    : undefined;
  const licenseKeys = journal.licenses || [];
  const licenseIcons =
    journal.licenses
      ?.map((license) => localizedData?.licenses?.[license])
      .filter((icon): icon is NonNullable<typeof icon> => Boolean(icon)) || [];
  const publishingModelIcon = journal.publishing_model
    ? localizedData?.publishing_models?.[journal.publishing_model]
    : undefined;
  const publishingModelUrl =
    (publishingModelIcon?.url?.trim() ||
      branding?.publishing_model_url?.trim()) &&
    journal.publishing_model &&
    locale
      ? generatePublishingModelLink(journal.publishing_model, locale, slug)
      : "";
  const linkTarget = branding?.open_in_new_window ? "_blank" : "_self";
  const multipleIcon =
    localizedData?.licenses?.["MULTIPLE"] ||
    localizedData?.multiple_licenses_icon;
  const singleLicense = journal.licenses?.length === 1;

  const licenseIcon = !journal.licenses?.length
    ? undefined
    : singleLicense
      ? licenseIcons[0]
      : multipleIcon;

  const paymentCellClickable = Boolean(
    showAgreementModal || journal.payment_url,
  );
  const singleLicenseUrl =
    singleLicense && licenseKeys[0] && locale
      ? generateLicenseLink(licenseKeys[0], locale, slug)
      : "";
  const licenseCellClickable = licenseIcon
    ? singleLicense
      ? singleLicenseUrl !== "" && singleLicenseUrl !== "#"
      : true
    : false;

  const paymentLabel = paymentIcon
    ? [paymentIcon.name, paymentIcon.description?.trim()]
        .filter(Boolean)
        .join(" – ")
    : undefined;
  const publishingModelLabel = publishingModelIcon
    ? [publishingModelIcon.name, publishingModelIcon.description?.trim()]
        .filter(Boolean)
        .join(" – ")
    : undefined;

  // Opening the pop-up is recorded as agreement_description. The payment link
  // inside it is recorded as payments.
  const openAgreementModal = () => {
    setIsAgreementOpen(true);
    trackUserEvent(slug, "d", "payment", {
      journal: journal._id,
      agreement: journal.agreement_id,
    }).catch(() => {});
  };

  const PaymentLinkElement = ({ children }: { children: React.ReactNode }) => {
    if (showAgreementModal) {
      return (
        <button
          ref={(el) => {
            paymentButtonRef.current = el;
            paymentTooltip.setTriggerRef(el);
          }}
          type="button"
          aria-haspopup="dialog"
          aria-label={paymentLabel}
          className={`paymentModalButton clickable-icon-cell min-h-[44px] min-w-[44px] tooltip ${
            paymentTooltip.isSuppressed ? "tooltip-escape-hidden" : ""
          }`}
          onClick={openAgreementModal}
          onKeyDown={paymentTooltip.onKeyDown}
          onBlur={paymentTooltip.resetSuppressed}
          onMouseLeave={paymentTooltip.resetSuppressed}
        >
          {children}
        </button>
      );
    }
    if (journal.payment_url) {
      return (
        <a
          ref={paymentTooltip.setTriggerRef}
          href={generatePaymentLink(journal._id || "", slug)}
          target={journal._id ? linkTarget : undefined}
          aria-label={paymentLabel}
          className={`clickable-icon-cell min-h-[44px] min-w-[44px] tooltip ${
            paymentTooltip.isSuppressed ? "tooltip-escape-hidden" : ""
          }`}
          onKeyDown={paymentTooltip.onKeyDown}
          onBlur={paymentTooltip.resetSuppressed}
          onMouseLeave={paymentTooltip.resetSuppressed}
        >
          {children}
        </a>
      );
    } else {
      return (
        <div
          ref={paymentTooltip.setTriggerRef}
          className={`min-h-[44px] min-w-[44px] tooltip inline-block ${
            paymentTooltip.isSuppressed ? "tooltip-escape-hidden" : ""
          }`}
          tabIndex={0}
          onKeyDown={paymentTooltip.onKeyDown}
          onBlur={paymentTooltip.resetSuppressed}
          onMouseLeave={paymentTooltip.resetSuppressed}
        >
          {children}
        </div>
      );
    }
  };

  const JournalElement = ({ children }: { children: React.ReactNode }) => {
    if (journal.url) {
      return (
        <a
          title={localizedData?.journal_link_tooltip}
          href={generateJournalLink(journal._id || "", slug)}
          target={journal._id ? linkTarget : undefined}
          className="min-h-[44px] min-w-[44px] block"
        >
          {children}
        </a>
      );
    } else {
      return (
        <div
          title={localizedData?.journal_link_tooltip}
          className="min-h-[44px] min-w-[44px] tooltip inline-block"
        >
          {children}
        </div>
      );
    }
  };

  const PublishingModelElement = ({
    children,
  }: {
    children: React.ReactNode;
  }) => {
    if (publishingModelUrl) {
      return (
        <a
          ref={publishingModelTooltip.setTriggerRef}
          aria-label={publishingModelLabel}
          className={`clickable-icon-cell tooltip min-h-[44px] min-w-[44px] ${
            publishingModelTooltip.isSuppressed ? "tooltip-escape-hidden" : ""
          }`}
          href={publishingModelUrl}
          target={linkTarget}
          rel={linkTarget === "_blank" ? "noopener noreferrer" : undefined}
          onKeyDown={publishingModelTooltip.onKeyDown}
          onBlur={publishingModelTooltip.resetSuppressed}
          onMouseLeave={publishingModelTooltip.resetSuppressed}
        >
          {children}
        </a>
      );
    }

    return (
      <div
        ref={publishingModelTooltip.setTriggerRef}
        className={`tooltip min-h-[44px] min-w-[44px] inline-block ${
          publishingModelTooltip.isSuppressed ? "tooltip-escape-hidden" : ""
        }`}
        tabIndex={0}
        onKeyDown={publishingModelTooltip.onKeyDown}
        onBlur={publishingModelTooltip.resetSuppressed}
        onMouseLeave={publishingModelTooltip.resetSuppressed}
      >
        {children}
      </div>
    );
  };

  const qualityList = branding?.extensions?.quality_lists;
  const hasQualityExtension = Boolean(qualityList && qualityList.length > 0);

  const cells: Record<ColumnKey, React.ReactNode> = {
    quality: (
      <td className="quality-cell" key="quality">
        {qualityList?.map((system) => (
          <QualityScoreEntry
            key={system}
            system={system}
            score={journal.quality_scores?.[system]}
            localizedData={localizedData}
            linkTarget={linkTarget}
            orgSlug={slug}
          />
        ))}
      </td>
    ),
    imprint: (
      <td className="imprint-cell" key="imprint">
        {journal.imprint}
      </td>
    ),
    payment: (
      <td
        className={`payment-cell ${paymentCellClickable ? "is-clickable" : ""}`}
        key="payment"
      >
        {paymentIcon && (
          <PaymentLinkElement>
            <span className="icon-tt">
              <Icon
                icon={paymentIcon}
                width={60}
                height={60}
                showArrow={Boolean(showAgreementModal || journal.payment_url)}
                alt={[paymentIcon.name, paymentIcon.description?.trim()]
                  .filter(Boolean)
                  .join(" – ")}
              />
              <span className="tooltiptext" aria-hidden="true">
                {paymentIcon.description?.trim()}
              </span>
            </span>
          </PaymentLinkElement>
        )}
      </td>
    ),
    license: (
      <td
        className={`license-cell ${licenseCellClickable ? "is-clickable" : ""}`}
        key="license"
      >
        {licenseIcon &&
          licenseIcons &&
          (singleLicense ? (
            <LicenseCell
              icon={licenseIcon}
              linkTarget={linkTarget}
              licenseKey={licenseKeys[0]}
              locale={locale}
              slug={slug}
            />
          ) : (
            <MultiLicenseCell
              icons={licenseIcons}
              linkTarget={linkTarget}
              multipleIcon={multipleIcon}
              licenseKeys={licenseKeys}
              locale={locale}
              slug={slug}
            />
          ))}
      </td>
    ),
    publishingModel: (
      <td
        className={`publishing-model-cell ${publishingModelUrl ? "is-clickable" : ""}`}
        key="publishingModel"
      >
        <PublishingModelElement>
          <div className="publishing-model-cell-inner">
            {publishingModelIcon && (
              <span className="icon-tt">
                <Icon
                  icon={publishingModelIcon}
                  width={40}
                  height={60}
                  showArrow={Boolean(publishingModelUrl)}
                  alt={[
                    publishingModelIcon.name,
                    publishingModelIcon.description?.trim(),
                  ]
                    .filter(Boolean)
                    .join(" – ")}
                />
                <span className="tooltiptext" aria-hidden="true">
                  {publishingModelIcon.description?.trim()}
                </span>
              </span>
            )}
            <span className="publishing-model-name" aria-hidden="true">
              {publishingModelIcon?.name || journal.publishing_model}
            </span>
          </div>
        </PublishingModelElement>
      </td>
    ),
  };

  const orderedColumns = resolveColumnOrder(
    branding?.column_order,
    hasQualityExtension,
  );
  const columnCount = 1 + orderedColumns.length;

  return (
    <>
      <tr
        className={`resultRow ${
          journal.extra_info?.trim() ? "" : "withBorder"
        }`}
      >
        <td className="journal-name-cell">
          <div>
            <JournalElement>{journal.name}</JournalElement>
            <LibraryBadges
              branding={branding}
              journalCollections={journal.collections}
              collectionsMap={branding?.collections}
              i18nData={i18nData}
            />
          </div>
        </td>
        {orderedColumns.map((key) => cells[key])}
      </tr>
      {journal.extra_info && journal.extra_info.trim().length > 0 && (
        <CollapsibleRow
          collapsedTitle={localizedData?.show_notes || "Show notes"}
          expandedTitle={localizedData?.hide_notes || "Hide notes"}
          columnCount={columnCount}
        >
          <Linkify
            options={{
              target: branding?.open_in_new_window ? "_blank" : "_self",
              rel: branding?.open_in_new_window
                ? "noopener noreferrer"
                : undefined,
            }}
          >
            {journal.extra_info}
          </Linkify>
        </CollapsibleRow>
      )}
      {showAgreementModal && isAgreementOpen && (
        <AgreementModal
          open={isAgreementOpen}
          onClose={() => setIsAgreementOpen(false)}
          journalName={journal.name}
          journalUrl={
            journal._id ? generateJournalLink(journal._id, slug) : undefined
          }
          journalId={journal._id}
          slug={slug}
          agreementId={journal.agreement_id!}
          localizedData={localizedData}
          linkTarget={linkTarget}
          returnFocusRef={paymentButtonRef}
        />
      )}
    </>
  );
});

export default Row;
